import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, User, Mail, Phone, Code, Smartphone, Palette, Cpu, Sparkles } from 'lucide-react';
import { useEnquiry } from '../../context/EnquiryContext';
import { supabase } from '../../supabaseClient';
import { sendLeadNotification } from '../../services/emailService';

const Enquiry = () => {
    const { isOpen, toggleEnquiry, closeEnquiry } = useEnquiry();
    const [formData, setFormData] = useState({
        name: '',
        type: 'company', // company or individual
        services: [],
        email: '',
        phone: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null

    const servicesList = [
        // Technical Services
        { id: 'web', label: 'Web Development', icon: <Code size={18} /> },
        { id: 'app', label: 'Mobile App', icon: <Smartphone size={18} /> },
        { id: 'uiux', label: 'UI/UX Design', icon: <Palette size={18} /> },
        { id: 'ai', label: 'AI Solutions', icon: <Cpu size={18} /> },

        // Industry Solutions
        { id: 'enterprise', label: 'Enterprise Solutions', icon: <Code size={18} /> },
        { id: 'startups', label: 'Startup Solutions', icon: <Sparkles size={18} /> },
        { id: 'retail', label: 'Retail & E-commerce', icon: <Smartphone size={18} /> },
        { id: 'saas', label: 'Technology & SaaS', icon: <Cpu size={18} /> },

        { id: 'other', label: 'Other', icon: <Sparkles size={18} /> },
    ];

    const toggleService = (id) => {
        setFormData(prev => ({
            ...prev,
            services: prev.services.includes(id)
                ? prev.services.filter(s => s !== id)
                : [...prev.services, id]
        }));
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setSubmitStatus(null);

        try {
            const leadData = {
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                services: formData.services,
                message: formData.message,
                client_type: formData.type,
                form_source: 'enquiry_modal'
            };

            let dbSuccess = false;
            let emailSuccess = false;

            // 1. Try Saving to Supabase
            try {
                const { error } = await supabase
                    .from('leads')
                    .insert([leadData]);

                if (error) throw error;
                dbSuccess = true;
            } catch (dbError) {
                console.error('Supabase DB Error:', dbError);
                // Continue...
            }

            // 2. Try sending email
            const emailResult = await sendLeadNotification(leadData);
            if (emailResult.success) {
                console.log('Email notification sent successfully.');
                emailSuccess = true;
            } else {
                console.warn('Email notification failed:', emailResult.message || emailResult.error);
            }

            // 3. Fallback check
            if (!dbSuccess && !emailSuccess) {
                throw new Error('Failed to submit enquiry. Service temporarily unavailable.');
            }

            setSubmitStatus('success');
            setTimeout(() => {
                setSubmitStatus(null);
                closeEnquiry();
                setFormData({
                    name: '',
                    type: 'company',
                    services: [],
                    email: '',
                    phone: '',
                    message: ''
                });
            }, 2000);

        } catch (error) {
            console.error('Error submitting enquiry:', error);
            setSubmitStatus('error');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
            {/* Floating Button */}
            <motion.button
                className={`fixed bottom-8 right-8 z-50 flex items-center justify-center w-16 h-16 rounded-full shadow-lg cursor-pointer group ${isOpen ? 'bg-red-500' : 'bg-primary'}`}
                onClick={toggleEnquiry}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ type: "spring", stiffness: 260, damping: 20 }}
                style={{ backgroundColor: isOpen ? '#991B1B' : '#D6001C' }}
            >
                <AnimatePresence mode='wait'>
                    {isOpen ? (
                        <motion.div
                            key="close"
                            initial={{ rotate: -90, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            exit={{ rotate: 90, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <X className="text-white w-8 h-8" />
                        </motion.div>
                    ) : (
                        <motion.div
                            key="message"
                            initial={{ rotate: 90, opacity: 0 }}
                            animate={{ rotate: 0, opacity: 1 }}
                            exit={{ rotate: -90, opacity: 0 }}
                            transition={{ duration: 0.2 }}
                        >
                            <MessageCircle className="text-white w-8 h-8" />
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Pulse Effect */}
                {!isOpen && (
                    <span className="absolute -inset-1 rounded-full bg-[#D6001C] opacity-30 animate-ping pointer-events-none"></span>
                )}
            </motion.button>

            {/* Modal / Slide-over */}
            <AnimatePresence>
                {isOpen && (
                    <>
                        {/* Backdrop */}
                        <motion.div
                            className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={closeEnquiry}
                        />

                        {/* Panel */}
                        <motion.div
                            className="fixed right-0 top-0 bottom-0 w-full max-w-md bg-white/95 backdrop-blur-xl shadow-2xl z-50 overflow-y-auto border-l border-gray-100"
                            initial={{ x: "100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "100%" }}
                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        >
                            <div className="p-8 h-full flex flex-col">
                                {/* Close Button */}
                                <button
                                    onClick={closeEnquiry}
                                    className="absolute top-6 right-6 w-10 h-10 flex items-center justify-center rounded-full bg-gray-100 hover:bg-red-50 text-gray-600 hover:text-[#D6001C] transition-all duration-300 hover:rotate-90 group z-10"
                                    aria-label="Close enquiry form"
                                >
                                    <X size={20} className="group-hover:scale-110 transition-transform" />
                                </button>

                                <div className="mb-8">
                                    <h2 className="text-3xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-[#D6001C] to-[#8B0000] mb-2">
                                        Let's Build Something Amazing
                                    </h2>
                                    <p className="text-gray-500">
                                        Tell us about your project and we'll get back to you within 24 hours.
                                    </p>
                                </div>

                                {submitStatus === 'success' ? (
                                    <motion.div
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        className="flex-1 flex flex-col items-center justify-center text-center p-6 space-y-4"
                                    >
                                        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center text-green-500 mb-4">
                                            <Send size={40} />
                                        </div>
                                        <h3 className="text-2xl font-bold text-gray-800">Message Sent!</h3>
                                        <p className="text-gray-600">Thank you for reaching out. We're excited to connect with you.</p>
                                    </motion.div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="space-y-6 flex-1">

                                        {/* Type Selection */}
                                        <div className="flex p-1 bg-gray-100 rounded-xl relative">
                                            <div className="absolute inset-y-1 w-1/2 bg-white rounded-lg shadow-sm transition-all duration-300 ease-out"
                                                style={{ transform: formData.type === 'individual' ? 'translateX(100%)' : 'translateX(0%)' }} />
                                            <button
                                                type="button"
                                                onClick={() => setFormData({ ...formData, type: 'company' })}
                                                className={`relative z-10 w-1/2 py-2 text-sm font-semibold transition-colors duration-300 ${formData.type === 'company' ? 'text-[#D6001C]' : 'text-gray-500'}`}
                                            >
                                                Company
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setFormData({ ...formData, type: 'individual' })}
                                                className={`relative z-10 w-1/2 py-2 text-sm font-semibold transition-colors duration-300 ${formData.type === 'individual' ? 'text-[#D6001C]' : 'text-gray-500'}`}
                                            >
                                                Individual
                                            </button>
                                        </div>

                                        {/* Name */}
                                        <div className="space-y-1">
                                            <label className="text-sm font-medium text-gray-700 ml-1">Full Name</label>
                                            <div className="relative group">
                                                <User className="absolute left-4 top-3.5 text-gray-400 group-focus-within:text-[#D6001C] transition-colors" size={20} />
                                                <input
                                                    type="text"
                                                    name="name"
                                                    required
                                                    value={formData.name}
                                                    onChange={handleChange}
                                                    className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#D6001C] focus:ring-4 focus:ring-[#D6001C]/10 transition-all outline-none"
                                                    placeholder="John Doe"
                                                />
                                            </div>
                                        </div>

                                        {/* Contact Info */}
                                        <div className="grid grid-cols-1 gap-4">
                                            <div className="space-y-1">
                                                <label className="text-sm font-medium text-gray-700 ml-1">Email Address</label>
                                                <div className="relative group">
                                                    <Mail className="absolute left-4 top-3.5 text-gray-400 group-focus-within:text-blue-600 transition-colors" size={20} />
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        required
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#D6001C] focus:ring-4 focus:ring-[#D6001C]/10 transition-all outline-none"
                                                        placeholder="john@example.com"
                                                    />
                                                </div>
                                            </div>

                                            <div className="space-y-1">
                                                <label className="text-sm font-medium text-gray-700 ml-1">Phone Number</label>
                                                <div className="relative group">
                                                    <Phone className="absolute left-4 top-3.5 text-gray-400 group-focus-within:text-blue-600 transition-colors" size={20} />
                                                    <input
                                                        type="tel"
                                                        name="phone"
                                                        required
                                                        value={formData.phone}
                                                        onChange={handleChange}
                                                        className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#D6001C] focus:ring-4 focus:ring-[#D6001C]/10 transition-all outline-none"
                                                        placeholder="+91 98765 43210"
                                                    />
                                                </div>
                                            </div>
                                        </div>

                                        {/* Services */}
                                        <div className="space-y-2">
                                            <label className="text-sm font-medium text-gray-700 ml-1">Services Needed</label>
                                            <div className="flex flex-wrap gap-2">
                                                {servicesList.map((service) => (
                                                    <button
                                                        key={service.id}
                                                        type="button"
                                                        onClick={() => toggleService(service.id)}
                                                        className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 border ${formData.services.includes(service.id)
                                                            ? 'bg-red-50 border-red-200 text-[#D6001C] shadow-sm'
                                                            : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50'
                                                            }`}
                                                    >
                                                        {service.icon}
                                                        {service.label}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Message / Details */}
                                        <div className="space-y-1">
                                            <label className="text-sm font-medium text-gray-700 ml-1">Additional Details</label>
                                            <textarea
                                                name="message"
                                                value={formData.message}
                                                onChange={handleChange}
                                                className="w-full p-4 bg-gray-50 border border-gray-200 rounded-xl focus:bg-white focus:border-[#D6001C] focus:ring-4 focus:ring-[#D6001C]/10 transition-all outline-none resize-none h-32"
                                                placeholder="Tell us more about your vision..."
                                            />
                                        </div>

                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="w-full py-4 bg-gradient-to-r from-[#D6001C] to-[#8B0000] text-white font-bold rounded-xl shadow-lg shadow-[#D6001C]/30 hover:shadow-[#D6001C]/50 transform hover:-translate-y-1 transition-all duration-300 disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                        >
                                            {isSubmitting ? (
                                                <div className="w-6 h-6 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                                            ) : (
                                                <>
                                                    <span>Send Enquiry</span>
                                                    <Send size={18} />
                                                </>
                                            )}
                                        </button>

                                        <p className="text-center text-xs text-gray-400">
                                            We respect your privacy. No spam, ever.
                                        </p>
                                    </form>
                                )}
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </>
    );
};

export default Enquiry;
